// Rapprochement saving déclaré / écart achats revu − prévu, avec lignes dépliables.

import { REC_LABELS, eur, eurSigned, recOf, type Affaire, type TraitementDto } from '@cockpit/shared';
import { Badge, Card, Table, Td, Th, cn } from '@cockpit/ui';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Fragment, useState } from 'react';
import { SavingDetail } from './Detail.js';
import { DoneButton, entLabel } from './bits.js';

const STT_TONE = { ok: 'success', up: 'danger', dn: 'warn' } as const;
const tone = (v: number) => (v < 0 ? 'text-success-700' : v > 0 ? 'text-danger-700' : 'text-ink-3');

function Compare({ e, s, mx }: { e: number; s: number; mx: number }) {
  const bar = (v: number, cls: string) => {
    const w = (Math.abs(v) / mx) * 50;
    return (
      <span className="relative block h-2 rounded-xs bg-surface-2">
        <span aria-hidden className="absolute inset-y-[-2px] left-1/2 w-px bg-gray-500" />
        <span
          aria-hidden
          className={cn('absolute inset-y-0 rounded-xs', cls)}
          style={v < 0 ? { right: '50%', width: `${w}%` } : { left: '50%', width: `${w}%` }}
        />
      </span>
    );
  };
  return (
    <div className="grid w-[170px] grid-cols-[72px_1fr] items-center gap-x-2 gap-y-1 text-[11px] text-ink-3">
      <span>écart achats</span>
      {bar(e, 'bg-navy-600')}
      <span>saving</span>
      {bar(s, 'bg-azur-500')}
    </div>
  );
}

export function RecTable({
  rows,
  showEnt,
  done,
  info,
  onToggle,
}: {
  rows: Affaire[];
  showEnt: boolean;
  done: ReadonlySet<string>;
  info: Map<string, TraitementDto>;
  onToggle: (code: string) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const mx = Math.max(1, ...rows.map((r) => Math.max(Math.abs(recOf(r).e), Math.abs(recOf(r).s))));
  return (
    <Card padded={false} className="px-2 py-3">
      <Table minWidth={1000}>
        <thead>
          <tr>
            <Th>Affaire</Th>
            <Th align="right">Achats prévus</Th>
            <Th align="right">Achats revus</Th>
            <Th align="right">Écart revu − prévu</Th>
            <Th align="right">Saving déclaré</Th>
            <Th>Comparaison</Th>
            <Th align="right">Non expliqué</Th>
            <Th align="right">Achats engagés</Th>
            <Th>Statut</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const c = recOf(r);
            const isOpen = open === r.code;
            const isDone = done.has(r.code);
            return (
              <Fragment key={r.code}>
                <tr
                  className={cn(
                    'cursor-pointer transition-colors duration-fast hover:bg-surface-2',
                    isDone && 'opacity-55',
                  )}
                  onClick={() => setOpen(isOpen ? null : r.code)}
                >
                  <Td className="max-w-[280px]">
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      className="flex items-start gap-1.5 text-left"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpen(isOpen ? null : r.code);
                      }}
                    >
                      {isOpen ? (
                        <ChevronDown size={16} strokeWidth={1.75} className="mt-0.5 shrink-0 text-ink-3" />
                      ) : (
                        <ChevronRight size={16} strokeWidth={1.75} className="mt-0.5 shrink-0 text-ink-3" />
                      )}
                      <span className="min-w-0">
                        <span className="block truncate font-display font-semibold text-ink">
                          {r.nom ?? r.cli}
                        </span>
                        <span className="block truncate text-[12px] text-ink-3">
                          {r.code}
                          {showEnt && ` · ${entLabel(r.ent)}`}
                          {r.type && ` · ${r.type}`} · {r.cli}
                        </span>
                      </span>
                    </button>
                  </Td>
                  <Td align="right">{eur(c.aP)}</Td>
                  <Td align="right">{eur(c.aR)}</Td>
                  <Td align="right" className={tone(c.e)}>
                    {eurSigned(c.e)}
                  </Td>
                  <Td align="right" className={tone(c.s)}>
                    {eurSigned(c.s)}
                  </Td>
                  <Td>
                    <Compare e={c.e} s={c.s} mx={mx} />
                  </Td>
                  <Td align="right" className={cn('font-bold', c.stt === 'ok' ? 'text-ink' : tone(c.x))}>
                    {eurSigned(c.x)}
                  </Td>
                  <Td align="right">{c.eng} %</Td>
                  <Td>
                    <Badge tone={STT_TONE[c.stt]} dot>
                      {REC_LABELS[c.stt]}
                    </Badge>
                  </Td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={9} className="border-b border-line-soft bg-surface-2/60 px-4 py-4">
                      {r.sav && <SavingDetail s={r.sav} />}
                      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-[13px] text-ink-3">
                        <span>
                          Marge {r.mp.toLocaleString('fr-FR')} % → {r.mr.toLocaleString('fr-FR')} % ·{' '}
                          {r.com ?? '—'} · cond. {r.cond ?? 'non affecté'}
                        </span>
                        <DoneButton done={isDone} info={info.get(r.code)} onToggle={() => onToggle(r.code)} />
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </Table>
    </Card>
  );
}
